<template>
  <form @submit="onSubmit">
    <BaseCard
      class="relative mx-auto w-full max-w-sm overflow-hidden"
    >
      <div class="space-y-6">
        <div
          class="col-span-4 flex w-full items-center justify-center"
        >
          <div
            class="bg-primary relative flex h-12 w-12 items-center justify-center rounded-full text-white"
            :style="{
              backgroundColor:
                cashierPreviewColor ?? undefined,
            }"
          >
            {{ cashierPreviewShortString }}
          </div>
        </div>
        <FormInputText name="username" label="Name" />

        <FormInputColor
          name="color"
          label="Display Color"
        />

        <input class="hidden" type="password" />
        <FormInputPassword
          type="pin"
          name="pin"
          label="Pin"
        />

        <FormSubmitButton
          :is-submitting="isSubmitting"
          class="w-full"
        >
          Save Cashier
        </FormSubmitButton>
      </div>

      <BaseInnerLoading
        v-if="cashierId && isRetrievingCashier"
      />
    </BaseCard>
  </form>

  <ErrorModal
    :is-open="isErrorModalOpen"
    message="Could not retrieve cashier"
    description="Something went wrong fetching cashier"
    :go-back-to="{
      name: 'manageCashiers',
    }"
    go-back-label="Return to cashier list"
  />
</template>

<script setup lang="ts">
import { ApiError } from "@/api"

const props = defineProps<{
  cashierId?: string
}>()

const router = useRouter()
const {
  handleSubmit,
  setErrors,
  setValues,
  values,
  isSubmitting,
} = useCreateUpdateCashierForm()

const onSubmit = handleSubmit(async (formValues) => {
  try {
    await (props.cashierId
      ? updateCashier(props.cashierId, { ...formValues })
      : createCashier(formValues))
    toast.success(
      props.cashierId
        ? "Cashier updated"
        : "Cashier created"
    )
    router.push({ name: "manageCashiers" })
  } catch (error) {
    if (error instanceof ApiError) {
      setErrors(error.requestErrors?.body ?? {})
    }
    toast.error(
      props.cashierId
        ? "Failed to update user"
        : "Failed to create user"
    )
    throw error
  }
})

const isErrorModalOpen = ref(false)
const isRetrievingCashier = ref(false)
onMounted(async () => {
  if (props.cashierId !== undefined) {
    try {
      isRetrievingCashier.value = true
      const cashier = await retrieveCashier(props.cashierId)
      setValues(cashier)
    } catch {
      isErrorModalOpen.value = true
    } finally {
      isRetrievingCashier.value = false
    }
  }
})

const cashierPreviewColor = computed(() => values.color)

const cashierPreviewShortString = computed(() =>
  buildShortString(values.username ?? "")
)
</script>
